import { Box, Button, Flex, Message } from '@workwhile/ui'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { DateRangePickerFilter } from 'components/Filter/DateRangePickerFilter'
import { IntFilter } from 'components/Filter/IntFilter'
import { StringFilter } from 'components/Filter/StringFilter'
import type { FiltersFormValues } from '../filtersSchema'

type EventLogsFiltersProps = {
  onApply: (values: FiltersFormValues) => void
}

// The filter chip components don't surface per-field errors, so walk the RHF error tree and collect every leaf `message` for a top-level summary.
const collectErrorMessages = (node: unknown): string[] => {
  const messages: string[] = []
  const visit = (value: unknown) => {
    if (!value || typeof value !== 'object') return
    const message = (value as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      messages.push(message)
      return
    }
    for (const child of Object.values(value)) visit(child)
  }
  visit(node)
  return Array.from(new Set(messages))
}

export const EventLogsFilters = ({ onApply }: EventLogsFiltersProps) => {
  const {
    handleSubmit,
    getValues,
    formState: { errors, isSubmitted },
  } = useFormContext<FiltersFormValues>()

  const errorMessages = isSubmitted ? collectErrorMessages(errors) : []

  // On invalid submit, still push form values to URL state so the URL mirrors the form; `buildListEventLogsParams` runs the same schema and keeps the query disabled.
  const onSubmit = handleSubmit(onApply, () => onApply(getValues()))

  return (
    <Flex
      as="form"
      flexDirection="row"
      flexWrap="wrap"
      gap={2}
      alignItems="center"
      onSubmit={onSubmit}
    >
      <DateRangePickerFilter<FiltersFormValues>
        dateRangeFieldPath="dateRange"
        label="Date range"
        filterKey="date-range"
      />
      <IntFilter<FiltersFormValues>
        fieldPath="companyId"
        label="Company ID"
        filterKey="company-id"
        inputLabel="Company ID"
      />
      <IntFilter<FiltersFormValues>
        fieldPath="userId"
        label="User ID"
        filterKey="user-id"
        inputLabel="User ID"
      />
      <IntFilter<FiltersFormValues>
        fieldPath="subjectUserId"
        label="Subject User ID"
        filterKey="subject-user-id"
        inputLabel="Subject User ID"
      />
      <IntFilter<FiltersFormValues>
        fieldPath="shiftId"
        label="Shift ID"
        filterKey="shift-id"
        inputLabel="Shift ID"
      />
      <IntFilter<FiltersFormValues>
        fieldPath="limit"
        label="Page size"
        filterKey="page-size"
        inputLabel="Page size (1–100)"
      />
      <StringFilter<FiltersFormValues>
        fieldPath="messageContains"
        label="Message contains"
        filterKey="message-contains"
        inputLabel="message substring (case-insensitive)"
        formatValue={(s) => (s.length > 48 ? `${s.slice(0, 45)}…` : s)}
      />
      <StringFilter<FiltersFormValues>
        fieldPath="eventType"
        label="Event types"
        filterKey="event-type"
        inputLabel="comma-separated event type ids (e.g. work, shift_create)"
      />
      <Button type="submit" variant="primary">
        Apply filters
      </Button>
      {errorMessages.length > 0 ? (
        <Box width="100%">
          <Message variant="error">
            {errorMessages.length === 1 ? (
              errorMessages[0]
            ) : (
              <Box as="ul" m={0} pl={3}>
                {errorMessages.map((message) => (
                  <Box as="li" key={message}>
                    {message}
                  </Box>
                ))}
              </Box>
            )}
          </Message>
        </Box>
      ) : null}
    </Flex>
  )
}
